import { useCallback, useEffect, useState } from "react";
import { Eye, Mail, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

const ST = { gonderildi: "text-emerald-700", gonderilmedi: "text-amber-700", hata: "text-rose-700" };

function Recipients({ d, onDone }) {
  const [list, setList] = useState(d.email.recipients || []);
  const [v, setV] = useState("");
  const save = async (next) => {
    try { const { data } = await api.put("/backup/recipients", { emails: next }); setList(data.recipients); toast.success("Alıcılar güncellendi"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="backup-recipients">
      <div className="flex flex-wrap gap-1.5">{list.map((r) => (
        <span key={r} className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded bg-slate-100" data-testid={`backup-recipient-${r}`}>{r}
          {d.is_super && <button type="button" onClick={() => save(list.filter((x) => x !== r))} data-testid={`backup-recipient-remove-${r}`}><X className="h-3 w-3" /></button>}</span>))}
        {list.length === 0 && <span className="text-[12px] text-slate-400">Alıcı yok</span>}</div>
      {d.is_super && <div className="flex gap-2">
        <Input type="email" placeholder="ornek@firma.com" value={v} onChange={(e) => setV(e.target.value)} className="h-8" data-testid="backup-recipient-input" />
        <Button size="sm" variant="outline" disabled={!v.includes("@")} onClick={() => { save([...list, v.trim()]); setV(""); }} data-testid="backup-recipient-add"><Plus className="h-3.5 w-3.5" /></Button>
      </div>}
    </div>
  );
}

export function WeeklyReportsCard({ d, onDone }) {
  const [items, setItems] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/backup/reports").then(({ data }) => setItems(data.items)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backup/weekly-report"); toast.success(`Özet oluşturuldu: ${data.label}`); load(); onDone(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const open = async (id) => { try { const { data } = await api.get(`/backup/reports/${id}`); setView(data); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="panel p-4" data-testid="weekly-reports-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4" />Haftalık Özetler</h3>
        {d.is_super && <Button size="sm" variant="outline" disabled={busy} onClick={create} data-testid="backup-weekly-report-button">Şimdi oluştur</Button>}
      </div>
      <div className={`text-[12px] mb-2 ${d.email.configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="weekly-email-status">{d.email.label} · Her Pazartesi 08:30</div>
      <Recipients d={d} onDone={onDone} />
      <div className="mt-3 divide-y divide-slate-100" data-testid="weekly-reports-list">
        {!items ? null : items.length === 0 ? <div className="text-[12px] text-slate-400 py-2">Henüz özet yok</div> : items.map((r) => (
          <div key={r.id} className="py-1.5 flex items-center gap-2 text-[12px]" data-testid={`weekly-report-${r.id}`}>
            <div className="flex-1 min-w-0"><div className="truncate">{formatDate(r.at)} · {r.subject}</div><div className={ST[r.status]}>{r.label}</div></div>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => open(r.id)} data-testid={`weekly-report-preview-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Önizle</Button>
          </div>))}
      </div>
      {view && <Dialog open onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl" data-testid="weekly-report-preview">
          <DialogHeader><DialogTitle className="text-[14px]">{view.subject}</DialogTitle></DialogHeader>
          <div className={`text-[12px] ${ST[view.status]}`}>{view.label} · Alıcılar: {view.recipients.join(", ") || "—"}</div>
          <iframe title="Önizleme" sandbox="" srcDoc={view.html} className="w-full h-[420px] border rounded" data-testid="weekly-report-iframe" />
        </DialogContent>
      </Dialog>}
    </div>
  );
}
